import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button, Popconfirm, Space, Alert, Spin, message, Tooltip, Empty,
} from 'antd';
import {
  getAutoUploadRules, createAutoUploadRule, updateAutoUploadRule,
  deleteAutoUploadRule, importAutoUploadRules, reorderAutoUploadRules,
  previewAutoUploadRules,
} from '../../../actions/autoUploadRules';
import { captureAnalytics } from '../../../actions/preload';
import { getDocumentCategories, getOrganizationDepartments } from '../../../actions/userProfile';
import { verifyFeatureAccess } from '../../../global/featureFlags';
import AddAutoUploadRuleModal from '../../modal/AddAutoUploadRuleModal';
import AutoUploadRulesTable from './AutoUploadRulesTable';
import AutoUploadRulesPreviewModal from './AutoUploadRulesPreviewModal';
import { buildAutoUploadRuleColumns } from './AutoUploadRuleColumns';

const countRuleCategories = (rule = {}) => {
  if (Array.isArray(rule.category_ids)) return rule.category_ids.length;
  if (Array.isArray(rule.categories)) return rule.categories.length;
  return 0;
};

const ruleAnalyticsProperties = (rule = {}) => ({
  rule_id: rule.id,
  action: rule.action,
  active: rule.active,
  source_type: rule.source_type || 'org_wide',
  category_match_mode: rule.category_match_mode,
  condition_match_mode: rule.condition_match_mode,
  categories_count: countRuleCategories(rule),
  conditions_count: (rule.conditions || []).length,
  auto_archive: !!rule.auto_archive,
  auto_archive_scope: rule.auto_archive ? rule.auto_archive_scope : null,
  document_defaults_configured: Object.keys(rule.document_defaults || {}).length,
});

class ManageAutoUploadRules extends Component {
  constructor(props) {
    super(props);
    this.modalGeneration = 0;
    this.state = {
      showModal: false,
      editingRule: null,
      submitting: false,
      saveError: null,
      canCreateTasks: false,
      showPreview: false,
      localRules: props.rules || [],
    };
  }

  async componentDidMount() {
    this.props.getAutoUploadRules();
    this.props.getDocumentCategories();
    this.props.getOrganizationDepartments();
    const canCreateTasks = await verifyFeatureAccess('task_auto_upload');
    this.setState({ canCreateTasks });
  }

  componentDidUpdate(prevProps) {
    const { rules } = this.props;
    if (prevProps.rules !== rules) {
      this.setState({ localRules: rules });
    }
  }

  openAdd = () => {
    captureAnalytics('auto_upload_rule_add_clicked', { rules_count: this.state.localRules.length });
    this.modalGeneration += 1;
    this.setState({
      showModal: true, editingRule: null, saveError: null,
    });
  };

  openEdit = (rule) => {
    captureAnalytics('auto_upload_rule_edit_clicked', ruleAnalyticsProperties(rule));
    this.modalGeneration += 1;
    this.setState({
      showModal: true, editingRule: rule, saveError: null,
    });
  };

  closeModal = () => {
    this.modalGeneration += 1;
    this.setState({
      showModal: false, editingRule: null, saveError: null,
    });
  };

  openPreview = async () => {
    captureAnalytics('auto_upload_rule_preview_opened', { rules_count: this.state.localRules.length });
    this.setState({ showPreview: true });
    await this.props.previewAutoUploadRules(10);
  };

  closePreview = () => this.setState({ showPreview: false });

  handleSubmit = async (payload, id) => {
    if (this.saveInFlight) return;
    this.saveInFlight = true;
    const { modalGeneration } = this;
    this.setState({ submitting: true, saveError: null });
    let result;
    try {
      result = id
        ? await this.props.updateAutoUploadRule(id, payload)
        : await this.props.createAutoUploadRule(payload);
    } catch {
      result = { error: true };
    } finally {
      this.saveInFlight = false;
      this.setState({ submitting: false });
    }
    if (modalGeneration !== this.modalGeneration) {
      if (!result?.error) this.props.getAutoUploadRules();
      return;
    }

    if (result && result.error) {
      this.setState({
        saveError: {
          status: result.response?.status,
          code: result.response?.data?.error_code,
          message: result.response?.data?.error || 'Could not save the rule',
        },
      });
      return;
    }
    message.success(id ? 'Rule updated' : 'Rule created');
    captureAnalytics(id ? 'auto_upload_rule_updated' : 'auto_upload_rule_created', {
      ...ruleAnalyticsProperties({ ...payload, id }),
      action_type: id ? 'update' : 'create',
    });
    this.closeModal();
    this.props.getAutoUploadRules();
  };

  handleDelete = async (rule) => {
    const result = await this.props.deleteAutoUploadRule(rule.id);
    if (result && result.error) {
      message.error('Could not delete the rule');
      return;
    }
    message.success('Rule deleted');
    captureAnalytics('auto_upload_rule_deleted', ruleAnalyticsProperties(rule));
    this.props.getAutoUploadRules();
  };

  handleToggleActive = async (rule) => {
    const result = await this.props.updateAutoUploadRule(rule.id, { active: !rule.active });
    if (result && result.error) {
      message.error('Could not update the rule');
      return;
    }
    captureAnalytics('auto_upload_rule_toggled', {
      ...ruleAnalyticsProperties(rule),
      active: !rule.active,
    });
    this.props.getAutoUploadRules();
  };

  handleImport = async () => {
    const result = await this.props.importAutoUploadRules();
    if (result && result.error) {
      message.error('Could not import previous settings');
      return;
    }
    message.success('Imported your previous auto-upload settings');
    captureAnalytics('auto_upload_rules_imported', { previous_rules_count: this.state.localRules.length });
  };

  handleReorder = async (newRules) => {
    // Optimistic reorder; the server response (full list) resyncs via componentDidUpdate.
    this.setState({ localRules: newRules });
    const result = await this.props.reorderAutoUploadRules(newRules.map((r) => r.id));
    if (result && result.error) {
      message.error('Could not reorder rules');
      this.props.getAutoUploadRules();
      return;
    }
    captureAnalytics('auto_upload_rules_reordered', { rules_count: newRules.length });
  };

  columns() {
    const { isAdmin } = this.props;
    return buildAutoUploadRuleColumns({
      isAdmin,
      rules: this.state.localRules,
      onEdit: this.openEdit,
      onDelete: this.handleDelete,
      onToggleActive: this.handleToggleActive,
    });
  }

  renderEmptyState() {
    const { isAdmin } = this.props;
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No auto-upload rules yet"
      >
        {isAdmin && (
          <Space>
            <Button type="primary" onClick={this.openAdd}>Add rule</Button>
            {this.renderImportAction()}
          </Space>
        )}
      </Empty>
    );
  }

  renderImportAction() {
    return (
      <Popconfirm
        title="Import your organization's previous auto-upload configuration?"
        onConfirm={this.handleImport}
      >
        <Tooltip title="Bring in your organization's previous auto-upload configuration">
          <Button>Import previous settings</Button>
        </Tooltip>
      </Popconfirm>
    );
  }

  render() {
    const {
      isLoading, isPreviewLoading, isAdmin, categories, emailSources, departments, preview, labels,
      documentDefaultFields, documentDefaultsCredentialId, documentDefaultsUnavailableReason, emaResults,
    } = this.props;
    const {
      showModal, showPreview, editingRule, submitting, canCreateTasks, localRules, saveError,
    } = this.state;

    const hasRules = localRules.length > 0;

    return (
      <div style={{ padding: 16 }}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Auto-upload rules"
          description="Control which received documents are automatically sent to your EMR — by fax line, email, department, or org-wide, filtered by document category and conditions. Rules run top to bottom — drag to reorder; the first match wins."
        />

        {isAdmin && (
          <Space style={{ marginBottom: 16 }}>
            <Button type="primary" onClick={this.openAdd}>Add rule</Button>
            <Button onClick={this.openPreview}>Preview against recent documents</Button>
            {this.renderImportAction()}
          </Space>
        )}

        {!hasRules ? (
          <Spin spinning={isLoading}>
            {this.renderEmptyState()}
          </Spin>
        ) : (
          <Spin spinning={isLoading}>
            <AutoUploadRulesTable
              rules={localRules}
              columns={this.columns()}
              isLoading={isLoading}
              onReorder={this.handleReorder}
              sortable={isAdmin}
            />
          </Spin>
        )}

        {showModal && (
          <AddAutoUploadRuleModal
            visible={showModal}
            editingRule={editingRule}
            categories={categories}
            emailSources={emailSources}
            labels={labels}
            documentDefaultFields={documentDefaultFields}
            documentDefaultsCredentialId={documentDefaultsCredentialId}
            documentDefaultsUnavailableReason={documentDefaultsUnavailableReason}
            emaResults={emaResults}
            saveError={saveError}
            departments={departments}
            canCreateTasks={canCreateTasks}
            confirmLoading={submitting}
            onCancel={this.closeModal}
            onSubmit={this.handleSubmit}
          />
        )}

        <AutoUploadRulesPreviewModal
          visible={showPreview}
          preview={preview}
          isLoading={isPreviewLoading}
          onClose={this.closePreview}
        />
      </div>
    );
  }
}

ManageAutoUploadRules.propTypes = {
  rules: PropTypes.array,
  preview: PropTypes.array,
  isLoading: PropTypes.bool,
  isPreviewLoading: PropTypes.bool,
  isAdmin: PropTypes.bool,
  categories: PropTypes.array,
  emailSources: PropTypes.array,
  labels: PropTypes.array,
  documentDefaultFields: PropTypes.array,
  documentDefaultsCredentialId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  documentDefaultsUnavailableReason: PropTypes.string,
  emaResults: PropTypes.shape({
    available: PropTypes.bool,
    default_enabled: PropTypes.bool,
    default_on_no_match: PropTypes.string,
  }),
  departments: PropTypes.array,
  getAutoUploadRules: PropTypes.func.isRequired,
  createAutoUploadRule: PropTypes.func.isRequired,
  updateAutoUploadRule: PropTypes.func.isRequired,
  deleteAutoUploadRule: PropTypes.func.isRequired,
  importAutoUploadRules: PropTypes.func.isRequired,
  reorderAutoUploadRules: PropTypes.func.isRequired,
  previewAutoUploadRules: PropTypes.func.isRequired,
  getDocumentCategories: PropTypes.func.isRequired,
  getOrganizationDepartments: PropTypes.func.isRequired,
};

ManageAutoUploadRules.defaultProps = {
  rules: [],
  preview: [],
  isLoading: false,
  isPreviewLoading: false,
  isAdmin: false,
  categories: [],
  emailSources: [],
  labels: [],
  documentDefaultFields: [],
  documentDefaultsCredentialId: null,
  documentDefaultsUnavailableReason: null,
  emaResults: { available: false },
  departments: [],
};

const mapStateToProps = (state) => ({
  rules: state.autoUploadRules.rules,
  emailSources: state.autoUploadRules.emailSources,
  labels: state.autoUploadRules.labels,
  documentDefaultFields: state.autoUploadRules.documentDefaultFields,
  documentDefaultsCredentialId: state.autoUploadRules.documentDefaultsCredentialId,
  documentDefaultsUnavailableReason: state.autoUploadRules.documentDefaultsUnavailableReason,
  emaResults: state.autoUploadRules.emaResults,
  preview: state.autoUploadRules.preview,
  isLoading: state.autoUploadRules.isLoading,
  isPreviewLoading: state.autoUploadRules.isPreviewLoading,
  isAdmin: state.userProfile.isAdmin,
  categories: state.userProfile.allDocumentCategories?.categories || [],
  departments: state.userProfile.allOrganizationDepartments,
});

const mapDispatchToProps = {
  getAutoUploadRules,
  createAutoUploadRule,
  updateAutoUploadRule,
  deleteAutoUploadRule,
  importAutoUploadRules,
  reorderAutoUploadRules,
  previewAutoUploadRules,
  getDocumentCategories,
  getOrganizationDepartments,
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageAutoUploadRules);

export {
  ManageAutoUploadRules as UnconnectedManageAutoUploadRules,
  ruleAnalyticsProperties,
};
